import { useState } from 'react';
import {
  formatExhibitDays,
  hallOfBooths2026,
  type DaySummary,
  type ExhibitorDayState,
} from '@nt-tokyo/shared';
import { formatJstDateLabel, formatJstTime, type KioskData } from '@/lib/kiosk';
import ConfirmDialog from './ConfirmDialog';
import ExhibitorCard from './ExhibitorCard';

// 未受付の一覧 (配置番号順)。スタッフが受付で見たり、端末を持って巡回したりする。
// 各行から「受付」(ブースにいた) と「不在」(いなかった) を記録できる。
// 遅刻連絡 (青) / 巡回で不在を確認済み (黄) / まだ見ていない (白) を色で分ける。
export default function KioskPendingList({
  kiosk,
  summary,
  recordable,
  onBack,
}: {
  kiosk: KioskData;
  summary: DaySummary;
  // 会期外 (テスト表示) は記録しない
  recordable: boolean;
  onBack: () => void;
}) {
  const [target, setTarget] = useState<{ state: ExhibitorDayState; action: 'checkin' | 'absent' } | null>(null);

  const confirm = () => {
    if (!target) return;
    if (recordable) {
      kiosk.addRecord({
        exhibitorId: target.state.exhibitor.id,
        kind: target.action === 'checkin' ? 'checkin' : 'absent_seen',
        source: 'list',
      });
    }
    setTarget(null);
  };

  const subCounts = [
    summary.lateCount > 0 ? `遅刻連絡 ${summary.lateCount}` : null,
    summary.absentSeenCount > 0 ? `不在 ${summary.absentSeenCount}` : null,
  ].filter(Boolean);

  return (
    <div className="fixed inset-0 z-40 flex flex-col overflow-hidden bg-[#f4f4f6]">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#d9d9de] bg-white px-4 py-3 sm:px-6">
        <div className="text-[22px] font-extrabold sm:text-[24px]">
          未受付の一覧
          <span className="ml-2 text-[#1f4fd6]">{summary.pending.length} 組</span>
          {subCounts.length > 0 && (
            <span className="ml-1 text-[16px] font-bold text-[#6b6b70]">（{subCounts.join('・')}）</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-[18px] text-[#6b6b70] sm:inline">{formatJstDateLabel(kiosk.today.jstDate)}</span>
          <button
            type="button"
            onClick={onBack}
            className="rounded-xl border-2 border-[#d9d9de] bg-white px-5 py-2 text-[20px] font-bold"
          >
            ‹ 戻る
          </button>
        </div>
      </div>
      {!recordable && (
        <div className="bg-[#fff4e0] px-6 py-2 text-[16px] font-bold text-[#a15c00]">
          会期外のためテスト表示です。受付・不在を押しても記録されません。
        </div>
      )}

      <div className="flex-1 overflow-auto px-2 py-2 sm:px-6">
        {summary.pending.length === 0 ? (
          <div className="py-16 text-center text-[22px] text-[#6b6b70]">未受付の出展者はいません</div>
        ) : (
          <>
            {/* iPad・PC は表、スマホはカード */}
            <table className="hidden w-full border-collapse text-[21px] min-[900px]:table">
              <thead>
                <tr className="text-left text-[16px] font-extrabold text-[#6b6b70]">
                  {['配置番号', '番号', '出展者名', '机', '椅子', '電源', '出展日', '状況', ''].map((h) => (
                    <th key={h} className="whitespace-nowrap border-b-2 border-[#d9d9de] px-2 py-2">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.pending.map((s) => (
                  <tr key={s.exhibitor.id} className={rowBg(s)}>
                    <td className="whitespace-nowrap border-b border-[#eee] px-2 py-2.5">
                      <span className="font-extrabold text-[#1f4fd6]">{s.exhibitor.boothNumbers.join(', ') || '—'}</span>
                      <span className="ml-1.5 text-[15px] font-bold text-[#3a3a40]">
                        {hallOfBooths2026(s.exhibitor.boothNumbers) ?? ''}
                      </span>
                    </td>
                    <td className="border-b border-[#eee] px-2 py-2.5 font-bold tabular-nums">{s.exhibitor.applicationNumber}</td>
                    <td className="border-b border-[#eee] px-2 py-2.5 font-bold leading-tight">{s.exhibitor.exhibitorName}</td>
                    <td className="whitespace-nowrap border-b border-[#eee] px-2 py-2.5 font-bold">
                      {s.exhibitor.tableCount > 0 ? `${s.exhibitor.tableCount}本` : 'なし'}
                    </td>
                    <td className="whitespace-nowrap border-b border-[#eee] px-2 py-2.5 font-bold">
                      {s.exhibitor.chairCount > 0 ? `${s.exhibitor.chairCount}脚` : 'なし'}
                    </td>
                    <td className="whitespace-nowrap border-b border-[#eee] px-2 py-2.5 font-bold">
                      {s.exhibitor.powerWatts > 0 ? `${s.exhibitor.powerWatts}VA` : 'なし'}
                    </td>
                    <td className="whitespace-nowrap border-b border-[#eee] px-2 py-2.5 text-[18px]">
                      {formatExhibitDays(s.exhibitor.exhibitDays)}
                    </td>
                    {/* 札 (遅刻連絡・不在) の有無で列の幅が変わらないよう、幅を固定した列に置く */}
                    <td className="w-[170px] border-b border-[#eee] px-2 py-2.5">
                      <StatusLabels state={s} />
                    </td>
                    <td className="w-[1%] whitespace-nowrap border-b border-[#eee] px-2 py-2.5 text-right">
                      <ActionButtons onCheckin={() => setTarget({ state: s, action: 'checkin' })} onAbsent={() => setTarget({ state: s, action: 'absent' })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex flex-col min-[900px]:hidden">
              {summary.pending.map((s) => (
                <div key={s.exhibitor.id} className={`border-b border-[#e5e5ea] px-3 py-3 ${rowBg(s)}`}>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[20px] font-extrabold text-[#1f4fd6]">{s.exhibitor.boothNumbers.join(', ') || '—'}</span>
                    <span className="text-[14px] font-bold">{hallOfBooths2026(s.exhibitor.boothNumbers) ?? ''}</span>
                    <span className="text-[13px] text-[#6b6b70]">No.{s.exhibitor.applicationNumber}</span>
                    <span className="ml-auto text-[13px] text-[#6b6b70]">{formatExhibitDays(s.exhibitor.exhibitDays)}</span>
                  </div>
                  <div className="text-[16px] font-bold">{s.exhibitor.exhibitorName}</div>
                  <div className="text-[13px] text-[#3a3a40]">
                    机 {s.exhibitor.tableCount > 0 ? s.exhibitor.tableCount : 'なし'} ／ 椅子{' '}
                    {s.exhibitor.chairCount > 0 ? s.exhibitor.chairCount : 'なし'} ／ 電源{' '}
                    {s.exhibitor.powerWatts > 0 ? `${s.exhibitor.powerWatts}VA` : 'なし'}
                  </div>
                  <div className="mt-2 flex items-center justify-end gap-2">
                    <div className="mr-auto">
                      <StatusLabels state={s} />
                    </div>
                    <ActionButtons onCheckin={() => setTarget({ state: s, action: 'checkin' })} onAbsent={() => setTarget({ state: s, action: 'absent' })} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {target && (
        <ConfirmDialog
          title={target.action === 'checkin' ? 'この出展者を受付しますか？' : 'ブースにいなかったことを記録しますか？'}
          confirmLabel={target.action === 'checkin' ? '受付する' : '不在を記録'}
          tone={target.action === 'checkin' ? 'ok' : 'neutral'}
          onConfirm={confirm}
          onCancel={() => setTarget(null)}
        >
          <ExhibitorCard exhibitor={target.state.exhibitor} />
        </ConfirmDialog>
      )}
    </div>
  );
}

function rowBg(s: ExhibitorDayState): string {
  if (s.late) return 'bg-[#eef2fd]';
  if (s.lastAbsentSeenAt) return 'bg-[#fff8e8]';
  return 'bg-white';
}

function StatusLabels({ state }: { state: ExhibitorDayState }) {
  return (
    <>
      {state.late && (
        <span className="mr-2 inline-block rounded-md border border-[#b9c6ee] bg-white px-2 py-0.5 text-[15px] font-extrabold text-[#1f4fd6]">
          遅刻連絡 {state.late.expectedTime ? `${state.late.expectedTime}頃` : '時刻不明'}
        </span>
      )}
      {state.lastAbsentSeenAt && (
        <span className="mr-2 inline-block rounded-md border border-[#e7c48a] bg-white px-2 py-0.5 text-[15px] font-extrabold text-[#a15c00]">
          不在 {formatJstTime(state.lastAbsentSeenAt)}
        </span>
      )}
    </>
  );
}

function ActionButtons({ onCheckin, onAbsent }: { onCheckin: () => void; onAbsent: () => void }) {
  return (
    <span className="inline-flex gap-2">
      <button
        type="button"
        onClick={onAbsent}
        className="whitespace-nowrap rounded-lg border-2 border-[#d9d9de] bg-white px-3.5 py-1.5 text-[17px] font-extrabold text-[#6b6b70]"
      >
        不在
      </button>
      <button type="button" onClick={onCheckin} className="whitespace-nowrap rounded-lg bg-[#127a3e] px-4 py-1.5 text-[17px] font-extrabold text-white">
        受付
      </button>
    </span>
  );
}
